import { useEffect } from 'react';

import { URLQueryParamNames } from '@src/types/urlQueryParamNames';
import { useAppDispatch } from '@store';
import { setIsFilterOpen } from '@store/verktoy/slice/verktoySlice';

import type { VerktoyFilterProps } from './types/verktoy-filter-props';
import type { VerktoyFilterState } from '@src/types/stateTypes';

import Spacer from '@helsenorge/designsystem-react/components/Spacer';

import Filters, { Status } from '@helsenorge/filter/components/filter';

import FagomradeDropdown from './controls/fagomrade-dropdown';
import VerktoyFilterSearch from './controls/filter-search';
import MalgruppeDropdown from './controls/malgruppe-dropdown';
import VerktoytypeDropdown from './controls/verktoytype-dropdown';
import { FilterSortOption, getCurrentFilterSelection, parseSortValue } from './helpers/filter-helpers';
import { FilterOptionType } from './types/filter-option-type';
import useVerktoyFilterState from '../../../hooks/useVerktoyFilterState';

export type Filters = {
  [key: string]: string | undefined | boolean;
};

const VerktoyFilter = ({ antallVerktoy, changeFilterFn, isLoading }: VerktoyFilterProps): React.JSX.Element | null => {
  const dispatch = useAppDispatch();

  const {
    resources,
    isFilterOpen,
    filters,
    updateFilters,
    activeFilters,
    setDelayedFilter,
    searchValueUrl,
    setSearchValueUrl,
    searchQuery,
    setSearchQuery,
    sortKey,
    setSortKey,
    fagomraader,
    fagomraderValgt,
    setFagomraderValgt,
    malgrupper,
    malgrupperValgt,
    setMalgrupperValgt,
    verktoytyper,
    verktoytyperValgt,
    setVerktoyTyperValgt,
    stotterAnonymOppstartChecked,
    valgtSortering,
    setValgtSortering,
    valgtSorterstigende,
    setValgtSorterstigende,
  } = useVerktoyFilterState();

  const onSearchClicked = (newFilters: VerktoyFilterState): void => {
    changeFilterFn(newFilters);
  };

  const callUpdateFilters = (): void => {
    updateFilters();
  };

  const handleSetIsFilterOpen = (isOpen: boolean): void => {
    dispatch(setIsFilterOpen({ isFilterOpen: isOpen }));
  };

  const onResetActiveFilters = (): void => {
    setMalgrupperValgt([]);
    setVerktoyTyperValgt([]);
    setFagomraderValgt([]);
    setVerktoyTyperValgt([]);
  };

  useEffect(() => {
    changeFilterFn(
      getCurrentFilterSelection({
        filters: filters ?? {},
        URLQueryParamNames,
        searchValueUrl,
        activeFilters: (activeFilters ?? []).filter(f => typeof f.value === 'string') as { name: string; value: string }[],
        fagomraader,
        malgrupper,
        verktoytyper,
        valgtSortering,
        valgtSorterstigende,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, valgtSortering, valgtSorterstigende]); // Obs: Kan ikke legge til alle deps her som anbefalt da vil skape en loop

  if (!resources) return null;
  return (
    <div className="verktoy__filters">
      <Spacer size="xl" />
      <Filters
        resources={{ filterTitle: resources?.verktoyFilterMobileToggleFilter, useFilter: resources?.verktoyFilterVisResultat }}
        statusText={`${antallVerktoy} ${resources?.verktoyFilterVerktoyUnit}`}
        activeFilters={activeFilters}
        status={isLoading ? Status.LoadingResults : undefined}
        showFilters={isFilterOpen}
        onResetActiveFilters={onResetActiveFilters}
        onFilterOpen={(showFilters: boolean | undefined) => handleSetIsFilterOpen(showFilters ?? false)}
        searchFilter={
          <VerktoyFilterSearch
            searchValueUrl={searchValueUrl}
            setSearchValueUrl={setSearchValueUrl}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            stotterAnonymOppstartChecked={stotterAnonymOppstartChecked}
            fagomraderValgt={fagomraderValgt}
            malgrupperValgt={malgrupperValgt}
            verktoytyperValgt={verktoytyperValgt}
            valgtSortering={valgtSortering}
            valgtSorterstigende={valgtSorterstigende}
            onSearchClicked={onSearchClicked}
          />
        }
        filterOptions={[
          {
            placeholder: resources?.verktoyFilterHelseomrade,
            label: resources?.verktoyFilterHelseomrade,
            type: FilterOptionType.Dropdown,
            onClose: () => callUpdateFilters(),
            children: fagomraader.map(fagomraade => (
              <FagomradeDropdown
                key={fagomraade.value}
                fagomraade={fagomraade}
                filters={Object.fromEntries(Object.entries(filters ?? {}).map(([k, v]) => [k, Boolean(v)]))}
                fagomraderValgt={fagomraderValgt}
                setFagomraderValgt={setFagomraderValgt}
                setDelayedFilter={setDelayedFilter}
              />
            )),
          },
          {
            placeholder: resources?.verktoyFilterMalgrupper,
            label: resources?.verktoyFilterMalgrupper,
            type: FilterOptionType.Dropdown,
            onClose: () => callUpdateFilters(),
            children: malgrupper.map(malgruppe => (
              <MalgruppeDropdown
                key={malgruppe.value}
                malgruppe={malgruppe}
                filters={Object.fromEntries(Object.entries(filters ?? {}).map(([k, v]) => [k, Boolean(v)]))}
                malgrupperValgt={malgrupperValgt}
                setMalgrupperValgt={setMalgrupperValgt}
                setDelayedFilter={setDelayedFilter}
              />
            )),
          },
          {
            placeholder: resources?.verktoyFilterVerktoytyper,
            label: resources?.verktoyFilterVerktoytyper,
            type: FilterOptionType.Dropdown,
            onClose: () => callUpdateFilters(),
            children: verktoytyper.map(verktoytype => (
              <VerktoytypeDropdown
                key={verktoytype.value}
                verktoytype={verktoytype}
                filters={Object.fromEntries(Object.entries(filters ?? {}).map(([k, v]) => [k, Boolean(v)]))}
                verktoytyperValgt={verktoytyperValgt}
                setVerktoyTyperValgt={setVerktoyTyperValgt}
                setDelayedFilter={setDelayedFilter}
              />
            )),
          },
        ]}
        footerContent={[
          {
            type: 'sort',
            label: resources?.verktoySorteringLabel,
            defaultValue: sortKey,
            onSort: (sortKey: string): void => {
              setSortKey(sortKey);
              const { field, direction } = parseSortValue(sortKey);

              setValgtSortering(field);
              setValgtSorterstigende(direction);
            },
            children: [
              { key: '1', value: FilterSortOption.VerktoyNavnAsc, label: resources?.verktoySorteringNavnAsc },
              { key: '2', value: FilterSortOption.VerktoyNavnDesc, label: resources?.verktoySorteringNavnDesc },
              { key: '3', value: FilterSortOption.PublisertDatoAsc, label: resources?.verktoySorteringPublisertDatoAsc },
              { key: '4', value: FilterSortOption.PublisertDatoDesc, label: resources?.verktoySorteringPublisertDatoDesc },
            ],
          },
        ]}
      />
    </div>
  );
};

export default VerktoyFilter;
